import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBrandLogo } from '../hooks/useBrandLogo';
import axios from 'axios';
import { toast } from 'sonner';
import MobileHeader from '../components/MobileHeader';
import {
  Users,
  Search,
  Plus,
  Pencil,
  Key,
  Shield,
  ShieldCheck,
  ShieldAlert,
  UserCheck,
  UserX,
  Eye,
  EyeOff,
  RefreshCw,
  Copy,
  Check,
  X,
  ChevronLeft,
  Clock,
  Mail,
  Phone,
  User,
  AlertTriangle,
  History,
  LogOut,
  Settings,
  Lock,
  Unlock,
  Heart,
  Trash2,
  RotateCcw,
  Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Configuración de roles
const ROLE_CONFIG = {
  gerencia: { 
    label: 'Gerencia', 
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    icon: ShieldCheck,
    description: 'Super Admin - Acceso total',
    soloAsignadoPorGerencia: true
  },
  admin: { 
    label: 'Admin', 
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: Shield,
    description: 'Administrador del sistema',
    soloAsignadoPorGerencia: true
  },
  asesor: { 
    label: 'Asesor', 
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: UserCheck,
    description: 'Asesor comercial'
  },
  diseñador: { 
    label: 'Diseñador', 
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: User,
    description: 'Diseñador de prendas'
  },
  vendedor: { 
    label: 'Vendedor', 
    color: 'bg-teal-50 text-teal-700 border-teal-200',
    icon: UserCheck,
    description: 'Vendedor autorizado'
  },
  asesor_diseñador: { 
    label: 'Asesor Diseñador', 
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    icon: UserCheck,
    description: 'Rol híbrido: asesor comercial + diseñador'
  },
  aliado: { 
    label: 'Aliado', 
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: Heart,
    description: 'Aliado referidor (Wedding planner, Hotel, etc.)'
  },
  cliente: { 
    label: 'Cliente', 
    color: 'bg-sky-50 text-sky-700 border-sky-200',
    icon: User,
    description: 'Cliente de sastrería (solo visualización)'
  }
};

// Estados de aprobación
const ESTADO_APROBACION_CONFIG = {
  pendiente: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700' },
  aprobado: { label: 'Aprobado', color: 'bg-emerald-100 text-emerald-700' },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-700' }
};

// Generador de contraseñas seguras
const generateSecurePassword = () => {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const special = '!@#$%^&*';
  
  let password = '';
  password += upper[Math.floor(Math.random() * upper.length)];
  password += lower[Math.floor(Math.random() * lower.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];
  
  const all = upper + lower + numbers + special;
  for (let i = 0; i < 8; i++) {
    password += all[Math.floor(Math.random() * all.length)];
  }
  
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

// Validador de fortaleza de contraseña
const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  
  if (score <= 2) return { score, label: 'Débil', color: 'bg-red-500' };
  if (score <= 4) return { score, label: 'Media', color: 'bg-amber-500' };
  return { score, label: 'Fuerte', color: 'bg-emerald-500' };
};

// Modal de cambio de contraseña
const PasswordChangeModal = ({ user, onClose, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const [copied, setCopied] = useState(false);

  const strength = getPasswordStrength(newPassword);
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const isValid = newPassword.length >= 8 && passwordsMatch;

  const handleGeneratePassword = () => {
    const pwd = generateSecurePassword();
    setNewPassword(pwd);
    setConfirmPassword(pwd);
    setShowPassword(true);
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(newPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Contraseña copiada al portapapeles');
  };

  const handleSubmit = async () => {
    if (!confirmStep) {
      setConfirmStep(true);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/admin/users/${user.id}/reset-password`, {
        new_password: newPassword,
        force_change_on_login: false // Siempre false - cambio directo sin pasos adicionales
      });
      toast.success('Contraseña actualizada correctamente. El usuario ya puede ingresar con la nueva contraseña asignada.');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scale-in overflow-hidden">
        {/* Header */}
        <div className="bg-black px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                <Key className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-serif text-lg text-white">Asignar Nueva Contraseña</h3>
                <p className="text-xs text-white/60">{user.username}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!confirmStep ? (
            <>
              {/* Generate Password Button */}
              <button
                onClick={handleGeneratePassword}
                className="w-full flex items-center justify-center gap-2 h-12 bg-warm-100 hover:bg-warm-200 text-warm-700 rounded-xl transition-colors mb-6"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="text-sm font-medium">Generar contraseña segura</span>
              </button>

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-warm-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 bg-white text-xs text-warm-400">o ingresa manualmente</span>
                </div>
              </div>

              {/* New Password */}
              <div className="mb-4">
                <label className="block text-[10px] uppercase tracking-wider text-warm-500 mb-2 font-medium">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full h-12 px-4 pr-20 bg-warm-50 border border-warm-200 rounded-xl text-sm focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/10 transition-all"
                    placeholder="Mín. 8 caracteres"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    {newPassword && (
                      <button
                        type="button"
                        onClick={handleCopyPassword}
                        className="w-8 h-8 flex items-center justify-center text-warm-400 hover:text-warm-600 transition-colors"
                        title="Copiar contraseña"
                      >
                        {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="w-8 h-8 flex items-center justify-center text-warm-400 hover:text-warm-600 transition-colors"
                      title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                {/* Password Strength */}
                {newPassword && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 h-1.5 bg-warm-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${strength.color} transition-all duration-300`}
                          style={{ width: `${(strength.score / 6) * 100}%` }}
                        />
                      </div>
                      <span className={`text-xs font-medium ${
                        strength.score <= 2 ? 'text-red-600' : 
                        strength.score <= 4 ? 'text-amber-600' : 'text-emerald-600'
                      }`}>
                        {strength.label}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="mb-6">
                <label className="block text-[10px] uppercase tracking-wider text-warm-500 mb-2 font-medium">
                  Confirmar Contraseña
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full h-12 px-4 bg-warm-50 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-all ${
                    confirmPassword && !passwordsMatch 
                      ? 'border-red-300 focus:border-red-400 focus:ring-red-100' 
                      : 'border-warm-200 focus:border-gold focus:ring-gold/10'
                  }`}
                  placeholder="Repetir contraseña"
                />
                {confirmPassword && !passwordsMatch && (
                  <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>
                )}
              </div>

              {/* Info Box - Cambio Directo */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-emerald-900">Cambio directo de contraseña</p>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      La contraseña será actualizada directamente. El usuario podrá iniciar sesión con la nueva contraseña asignada.
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Confirmation Step */
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-50 flex items-center justify-center">
                <Key className="w-8 h-8 text-emerald-600" />
              </div>
              <h4 className="font-serif text-xl text-black mb-2">Confirmar Asignación</h4>
              <p className="text-sm text-warm-600 mb-4">
                ¿Estás seguro de asignar una nueva contraseña a <strong>{user.username}</strong>?
              </p>
              <p className="text-xs text-warm-500 bg-emerald-50 p-3 rounded-lg border border-emerald-100">
                La contraseña anterior será reemplazada inmediatamente. El usuario podrá iniciar sesión directamente con la nueva contraseña.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={confirmStep ? () => setConfirmStep(false) : onClose}
            className="flex-1 h-12 bg-warm-100 hover:bg-warm-200 text-warm-700 rounded-xl text-sm font-medium transition-colors"
          >
            {confirmStep ? 'Volver' : 'Cancelar'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || loading}
            className="flex-1 h-12 bg-black hover:bg-warm-800 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : confirmStep ? (
              <>
                <Check className="w-4 h-4" />
                Asignar Contraseña
              </>
            ) : (
              'Continuar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal de edición de usuario
const EditUserModal = ({ user, onClose, onSuccess, isGerenciaUser }) => {
  const [formData, setFormData] = useState({
    username: user.username || '',
    email: user.email || '',
    phone: user.phone || '',
    role: user.role || 'vendedor',
    is_active: user.is_active !== false
  });
  const [loading, setLoading] = useState(false);

  // Roles disponibles según el usuario actual
  const rolesDisponibles = isGerenciaUser 
    ? ['vendedor', 'diseñador', 'asesor_diseñador', 'asesor', 'aliado', 'admin', 'gerencia'] // Gerencia puede asignar todos
    : ['vendedor', 'diseñador', 'asesor_diseñador', 'asesor', 'aliado']; // Admin solo puede asignar operativos

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API_URL}/admin/users/${user.id}`, formData);
      toast.success('Usuario actualizado exitosamente');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scale-in overflow-hidden">
        {/* Header */}
        <div className="bg-black px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                <Pencil className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-serif text-lg text-white">Editar Usuario</h3>
                <p className="text-xs text-white/60">{user.email}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-warm-500 mb-2 font-medium">
              Nombre de Usuario
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="w-full h-12 px-4 bg-warm-50 border border-warm-200 rounded-xl text-sm focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/10 transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-warm-500 mb-2 font-medium">
              Correo Electrónico
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full h-12 px-4 bg-warm-50 border border-warm-200 rounded-xl text-sm focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/10 transition-all"
              required
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-warm-500 mb-2 font-medium">
              Teléfono
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full h-12 px-4 bg-warm-50 border border-warm-200 rounded-xl text-sm focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/10 transition-all"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-warm-500 mb-2 font-medium">
              Rol
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full h-12 px-4 bg-warm-50 border border-warm-200 rounded-xl text-sm focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/10 transition-all"
            >
              {rolesDisponibles.map(role => (
                <option key={role} value={role}>
                  {ROLE_CONFIG[role]?.label || role}
                </option>
              ))}
            </select>
            {!isGerenciaUser && (formData.role === 'admin' || formData.role === 'gerencia') && (
              <p className="text-xs text-amber-600 mt-1">
                ⚠️ Solo Gerencia puede asignar roles de Admin o Gerencia
              </p>
            )}
          </div>

          <label className="flex items-center gap-3 p-4 bg-warm-50 border border-warm-200 rounded-xl cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-warm-300 text-emerald-600 focus:ring-emerald-500"
            />
            <div>
              <p className="text-sm font-medium text-warm-900">Usuario Activo</p>
              <p className="text-xs text-warm-500">Los usuarios inactivos no pueden iniciar sesión</p>
            </div>
          </label>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-12 bg-warm-100 hover:bg-warm-200 text-warm-700 rounded-xl text-sm font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-12 bg-black hover:bg-warm-800 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Guardar Cambios'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Vista de detalle de usuario
const UserDetailView = ({ user, onBack, onRefresh, isGerencia }) => {
  const [passwordHistory, setPasswordHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (isGerencia) {
      fetchPasswordHistory();
    }
  }, [user.id, isGerencia]);

  const fetchPasswordHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/${user.id}/password-history`);
      setPasswordHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching password history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleToggleForceChange = async () => {
    try {
      const response = await axios.put(`${API_URL}/admin/users/${user.id}/force-password-change`);
      toast.success(response.data.message);
      onRefresh();
      fetchPasswordHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar configuración');
    }
  };

  const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.asesor;
  const RoleIcon = roleConfig.icon;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Nunca';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="animate-fade-in">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-warm-500 hover:text-black mb-6 transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        <span className="text-sm font-medium">Volver a la lista</span>
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info Card */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-warm-100 shadow-sm overflow-hidden">
          {/* User Header */}
          <div className="bg-black px-6 py-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center overflow-hidden">
                {user.profile_photo ? (
                  <img src={user.profile_photo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-2xl font-medium">
                    {user.username?.charAt(0)?.toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-2xl text-white mb-1">{user.username}</h2>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-wider font-semibold rounded-full border ${roleConfig.color}`}>
                    <RoleIcon className="w-3 h-3" />
                    {roleConfig.label}
                  </span>
                  <span className={`inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-wider font-semibold rounded-full ${
                    user.is_active 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {user.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowEditModal(true)}
                className="h-10 px-4 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs uppercase tracking-wider font-medium transition-colors flex items-center gap-2"
              >
                <Pencil className="w-4 h-4" />
                Editar
              </button>
            </div>
          </div>

          {/* User Details */}
          <div className="p-6">
            <h3 className="text-[10px] uppercase tracking-wider text-warm-500 font-medium mb-4">
              Información de Contacto
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 bg-warm-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-white border border-warm-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-warm-500" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-warm-400">Email</p>
                  <p className="text-sm text-black font-medium">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-warm-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-white border border-warm-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-warm-500" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-warm-400">Teléfono</p>
                  <p className="text-sm text-black font-medium">{user.phone || 'No registrado'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-warm-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-white border border-warm-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-warm-500" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-warm-400">Último Acceso</p>
                  <p className="text-sm text-black font-medium">{formatDate(user.last_login)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-warm-50 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-white border border-warm-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-warm-500" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-warm-400">Creado</p>
                  <p className="text-sm text-black font-medium">{formatDate(user.created_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Panel - Only for Gerencia */}
        {isGerencia && (
          <div className="bg-white rounded-2xl border border-warm-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-warm-100">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-warm-500" />
                <h3 className="font-serif text-lg text-black">Seguridad de Acceso</h3>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Force Password Change Status */}
              <div className={`p-4 rounded-xl border ${
                user.force_password_change 
                  ? 'bg-amber-50 border-amber-200' 
                  : 'bg-warm-50 border-warm-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-warm-900">Cambio de contraseña pendiente</span>
                  {user.force_password_change ? (
                    <ShieldAlert className="w-5 h-5 text-amber-600" />
                  ) : (
                    <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  )}
                </div>
                <p className="text-xs text-warm-600">
                  {user.force_password_change 
                    ? 'El usuario debe cambiar su contraseña en el próximo inicio de sesión'
                    : 'No hay cambio de contraseña pendiente'
                  }
                </p>
              </div>

              {/* Action Buttons */}
              <button
                onClick={() => setShowPasswordModal(true)}
                className="w-full h-12 bg-black hover:bg-warm-800 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <Key className="w-4 h-4" />
                Cambiar Contraseña
              </button>

              <button
                onClick={handleToggleForceChange}
                className={`w-full h-12 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  user.force_password_change
                    ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200'
                    : 'bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200'
                }`}
              >
                {user.force_password_change ? (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    Desactivar Cambio Forzado
                  </>
                ) : (
                  <>
                    <ShieldAlert className="w-4 h-4" />
                    Forzar Cambio en Próximo Inicio
                  </>
                )}
              </button>

              {/* Password History */}
              <div className="pt-4 border-t border-warm-100">
                <h4 className="text-[10px] uppercase tracking-wider text-warm-500 font-medium mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Historial de Cambios
                </h4>
                
                {loadingHistory ? (
                  <div className="text-center py-4">
                    <div className="w-6 h-6 border-2 border-warm-200 border-t-warm-500 rounded-full animate-spin mx-auto" />
                  </div>
                ) : passwordHistory.length === 0 ? (
                  <p className="text-xs text-warm-400 text-center py-4">
                    No hay cambios registrados
                  </p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {passwordHistory.map((entry, index) => (
                      <div 
                        key={entry.id || index}
                        className="p-3 bg-warm-50 rounded-lg text-xs"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-warm-700">
                            {entry.action === 'password_changed' && 'Contraseña cambiada'}
                            {entry.action === 'force_change_enabled' && 'Cambio forzado activado'}
                            {entry.action === 'force_change_disabled' && 'Cambio forzado desactivado'}
                          </span>
                        </div>
                        <p className="text-warm-500">
                          Por: {entry.changed_by_username} • {formatDate(entry.timestamp)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showPasswordModal && (
        <PasswordChangeModal
          user={user}
          onClose={() => setShowPasswordModal(false)}
          onSuccess={() => {
            onRefresh();
            fetchPasswordHistory();
          }}
        />
      )}

      {showEditModal && (
        <EditUserModal
          user={user}
          onClose={() => setShowEditModal(false)}
          onSuccess={onRefresh}
          isGerenciaUser={isGerencia}
        />
      )}
    </div>
  );
};

// Componente principal
const AdminUsers = () => {
  const navigate = useNavigate();
  const { user, logout, hasPermission, isGerencia } = useAuth();
  const { logoUrl } = useBrandLogo();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('activos'); // activos, inactivos, eliminados, pendientes, todos
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  
  // Estado para eliminación
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteMotivo, setDeleteMotivo] = useState('');
  const [deletingUser, setDeletingUser] = useState(false);
  
  // Estado para gestión de permisos
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [permissionsData, setPermissionsData] = useState(null);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [customPermissions, setCustomPermissions] = useState({});

  useEffect(() => {
    if (!hasPermission('manage_users')) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [hasPermission, navigate, estadoFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/users`, {
        params: { 
          estado: estadoFilter,
          role: roleFilter || undefined  // Enviar filtro de rol al backend
        }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  // Cuando cambia el filtro de rol, recargar usuarios
  useEffect(() => {
    fetchUsers();
  }, [roleFilter]);

  const filteredUsers = users.filter(u => {
    const matchesSearch = !searchTerm || 
      u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    // El filtro de rol ahora se hace en el backend para clientes
    const matchesRole = roleFilter === 'cliente' || !roleFilter || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Función para eliminar usuario
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeletingUser(true);
    try {
      const response = await axios.delete(`${API_URL}/admin/usuarios/${userToDelete.id}`, {
        data: { motivo: deleteMotivo || 'Sin motivo especificado' }
      });
      
      toast.success(response.data.message);
      setShowDeleteModal(false);
      setUserToDelete(null);
      setDeleteMotivo('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    } finally {
      setDeletingUser(false);
    }
  };

  // Función para restaurar usuario
  const handleRestoreUser = async (userId) => {
    try {
      const response = await axios.post(`${API_URL}/admin/usuarios/${userId}/restaurar`);
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar usuario');
    }
  };

  // Función para aprobar usuario pendiente
  const handleAprobarUsuario = async (userId, username) => {
    try {
      const response = await axios.post(`${API_URL}/admin/usuarios/${userId}/aprobar`);
      toast.success(response.data.message || `Usuario ${username} aprobado`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar usuario');
    }
  };

  // Función para rechazar usuario pendiente
  const handleRechazarUsuario = async (userId, username, motivo = 'Solicitud rechazada') => {
    try {
      const response = await axios.post(`${API_URL}/admin/usuarios/${userId}/rechazar`, null, {
        params: { motivo }
      });
      toast.success(response.data.message || `Usuario ${username} rechazado`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar usuario');
    }
  };

  // Verificar si el usuario actual puede eliminar al usuario objetivo
  const canDeleteUser = (targetUser) => {
    if (!user || !targetUser) return false;
    if (user.id === targetUser.id) return false; // No puede eliminarse a sí mismo
    if (user.role !== 'gerencia' && user.role !== 'admin') return false;
    if (user.role === 'admin' && targetUser.role === 'gerencia') return false; // Admin no puede eliminar Gerencia
    if (targetUser.eliminado) return false; // Ya está eliminado
    return true;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Funciones de gestión de permisos
  const openPermissionsModal = async (targetUser) => {
    setPermissionsUser(targetUser);
    setShowPermissionsModal(true);
    setLoadingPermissions(true);
    
    try {
      const response = await axios.get(`${API_URL}/admin/users/${targetUser.id}/permisos`);
      setPermissionsData(response.data);
      setCustomPermissions(response.data.permisos_personalizados || {});
    } catch (error) {
      toast.error('Error al cargar permisos');
      setShowPermissionsModal(false);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const togglePermission = (key) => {
    if (!permissionsData) return;
    
    const baseValue = permissionsData.permisos_base[key];
    const currentValue = customPermissions.hasOwnProperty(key) 
      ? customPermissions[key] 
      : baseValue;
    
    const newCustomPermissions = { ...customPermissions };
    
    // Si el nuevo valor es igual al base, eliminar del custom
    if (!currentValue === baseValue) {
      delete newCustomPermissions[key];
    } else {
      newCustomPermissions[key] = !currentValue;
    }
    
    setCustomPermissions(newCustomPermissions);
  };

  const getPermissionValue = (key) => {
    if (customPermissions.hasOwnProperty(key)) {
      return customPermissions[key];
    }
    return permissionsData?.permisos_base[key] || false;
  };

  const isPermissionCustomized = (key) => {
    return customPermissions.hasOwnProperty(key);
  };

  const savePermissions = async () => {
    if (!permissionsUser) return;
    
    setSavingPermissions(true);
    try {
      await axios.put(`${API_URL}/admin/users/${permissionsUser.id}/permisos`, {
        permisos: customPermissions
      });
      toast.success(`Permisos actualizados para ${permissionsUser.username}`);
      setShowPermissionsModal(false);
      setPermissionsUser(null);
      setPermissionsData(null);
      setCustomPermissions({});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar permisos');
    } finally {
      setSavingPermissions(false);
    }
  };

  const resetPermissions = async () => {
    if (!permissionsUser) return;
    
    if (!window.confirm('¿Restaurar permisos al valor predeterminado del rol?')) return;
    
    setSavingPermissions(true);
    try {
      await axios.delete(`${API_URL}/admin/users/${permissionsUser.id}/permisos`);
      toast.success('Permisos restaurados a los valores del rol');
      setCustomPermissions({});
      
      // Recargar datos
      const response = await axios.get(`${API_URL}/admin/users/${permissionsUser.id}/permisos`);
      setPermissionsData(response.data);
    } catch (error) {
      toast.error('Error al restaurar permisos');
    } finally {
      setSavingPermissions(false);
    }
  };

  // Función para exportar usuarios a Excel
  const handleExportUsers = async () => {
    try {
      toast.loading('Generando archivo Excel...');
      const response = await axios.get(`${API_URL}/export/modulo/usuarios`, {
        responseType: 'blob'
      });
      
      // Crear enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `usuarios_jpm_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('Archivo descargado correctamente');
    } catch (error) {
      toast.dismiss();
      toast.error('Error al exportar usuarios');
    }
  };

  if (selectedUser) {
    return (
      <div className="min-h-screen bg-cream">
        <MobileHeader title="Detalle de Usuario" />
        
        {/* Desktop Header */}
        <header className="bg-black sticky top-0 z-20 hidden md:block">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between h-18 py-4">
              <div className="flex items-center gap-5">
                {logoUrl && (
                  <img src={logoUrl} alt="Logo" className="h-11 w-auto object-contain brightness-0 invert" />
                )}
                <div>
                  <h1 className="font-serif text-xl text-white tracking-wide">ATELIER <span className="text-white/40">—</span> JPM</h1>
                  <p className="text-[9px] uppercase tracking-[0.15em] text-white/50">Gestión de Usuarios</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => navigate('/dashboard')} className="px-4 py-2 text-[10px] uppercase tracking-wider text-white/70 hover:text-white hover:bg-white/10 transition-all rounded-lg">
                  Dashboard
                </button>
                <button onClick={handleLogout} className="px-3 py-2 text-white/40 hover:text-white hover:bg-white/10 transition-all rounded-lg">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
          <UserDetailView
            user={selectedUser}
            onBack={() => setSelectedUser(null)}
            onRefresh={() => {
              fetchUsers();
              const updated = users.find(u => u.id === selectedUser.id);
              if (updated) setSelectedUser(updated);
            }}
            isGerencia={isGerencia()}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <MobileHeader title="Gestión de Usuarios" />
      
      {/* Desktop Header */}
      <header className="bg-black sticky top-0 z-20 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-18 py-4">
            <div className="flex items-center gap-5">
              {logoUrl && (
                <img src={logoUrl} alt="Logo" className="h-11 w-auto object-contain brightness-0 invert" />
              )}
              <div>
                <h1 className="font-serif text-xl text-white tracking-wide">ATELIER <span className="text-white/40">—</span> JPM</h1>
                <p className="text-[9px] uppercase tracking-[0.15em] text-white/50">Gestión de Usuarios</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => navigate('/dashboard')} className="px-4 py-2 text-[10px] uppercase tracking-wider text-white/70 hover:text-white hover:bg-white/10 transition-all rounded-lg">
                Dashboard
              </button>
              <button onClick={handleLogout} className="px-3 py-2 text-white/40 hover:text-white hover:bg-white/10 transition-all rounded-lg">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h2 className="font-serif text-2xl md:text-3xl text-black">Gestión de Usuarios</h2>
            <p className="text-sm text-warm-500 mt-1">{users.length} usuarios registrados</p>
          </div>
          <button
            onClick={handleExportUsers}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
            data-testid="export-users-btn"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Exportar Excel
          </button>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-2xl border border-warm-100 shadow-sm p-4 md:p-5 mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-400" />
              <input
                type="text"
                placeholder="Buscar por nombre, correo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-12 pl-11 pr-4 bg-warm-50 border border-warm-100 rounded-xl text-sm placeholder:text-warm-400 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/10 transition-all"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="h-12 px-4 bg-warm-50 border border-warm-100 rounded-xl text-sm focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/10 transition-all min-w-[150px]"
            >
              <option value="">Todos los roles</option>
              <option value="gerencia">Gerencia</option>
              <option value="admin">Admin</option>
              <option value="asesor">Asesor</option>
              <option value="diseñador">Diseñador</option>
              <option value="vendedor">Vendedor</option>
              <option value="asesor_diseñador">Asesor Diseñador</option>
              <option value="aliado">Aliado</option>
              <option value="cliente">Cliente</option>
            </select>
            
            <select
              value={estadoFilter}
              onChange={(e) => setEstadoFilter(e.target.value)}
              className="h-12 px-4 bg-warm-50 border border-warm-100 rounded-xl text-sm focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/10 transition-all min-w-[150px]"
            >
              <option value="activos">Activos</option>
              <option value="inactivos">Inactivos</option>
              <option value="pendientes">Pendientes</option>
              <option value="eliminados">Eliminados</option>
              <option value="todos">Todos</option>
            </select>
          </div>
        </div>

        {/* Users List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-2 border-warm-200 border-t-black rounded-full animate-spin mb-5" />
            <p className="text-xs uppercase tracking-wider text-warm-400">Cargando usuarios...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="bg-white rounded-2xl border border-warm-100 shadow-sm py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-warm-100 flex items-center justify-center">
              <Users className="w-8 h-8 text-warm-400" />
            </div>
            <p className="text-warm-600 mb-1">No se encontraron usuarios</p>
            <p className="text-sm text-warm-400">Intenta con otros términos de búsqueda</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-warm-100 shadow-sm overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-warm-100">
                    <th className="px-6 py-4 text-left text-[10px] uppercase tracking-wider text-warm-500 font-medium">Usuario</th>
                    <th className="px-6 py-4 text-left text-[10px] uppercase tracking-wider text-warm-500 font-medium">Contacto</th>
                    <th className="px-6 py-4 text-left text-[10px] uppercase tracking-wider text-warm-500 font-medium">Rol</th>
                    <th className="px-6 py-4 text-left text-[10px] uppercase tracking-wider text-warm-500 font-medium">Estado</th>
                    <th className="px-6 py-4 text-left text-[10px] uppercase tracking-wider text-warm-500 font-medium">Último Acceso</th>
                    <th className="px-6 py-4 text-right text-[10px] uppercase tracking-wider text-warm-500 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-warm-100">
                  {filteredUsers.map((u, index) => {
                    const roleConfig = ROLE_CONFIG[u.role] || ROLE_CONFIG.asesor;
                    const RoleIcon = roleConfig.icon;
                    return (
                      <tr 
                        key={u.id}
                        className="hover:bg-warm-50/50 transition-colors animate-fade-up"
                        style={{ animationDelay: `${index * 30}ms` }}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-warm-100 flex items-center justify-center overflow-hidden">
                              {u.profile_photo ? (
                                <img src={u.profile_photo} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-warm-600 font-medium">
                                  {u.username?.charAt(0)?.toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-black">{u.username}</p>
                              {u.force_password_change && (
                                <span className="inline-flex items-center gap-1 text-[9px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                  <ShieldAlert className="w-3 h-3" />
                                  Cambio pendiente
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-warm-700">{u.email}</p>
                          {u.phone && <p className="text-xs text-warm-400">{u.phone}</p>}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-wider font-semibold rounded-full border ${roleConfig.color}`}>
                            <RoleIcon className="w-3 h-3" />
                            {roleConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {u.eliminado ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold rounded-full bg-zinc-100 text-zinc-600">
                              <Trash2 className="w-3 h-3" />
                              Eliminado
                            </span>
                          ) : u.estado_aprobacion === 'pendiente' ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold rounded-full bg-amber-50 text-amber-700">
                              <Clock className="w-3 h-3" />
                              Pendiente
                            </span>
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold rounded-full ${
                              u.is_active 
                                ? 'bg-emerald-50 text-emerald-700' 
                                : 'bg-red-50 text-red-700'
                            }`}>
                              {u.is_active ? 'Activo' : 'Inactivo'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-warm-500">
                          {u.eliminado && u.fecha_eliminacion 
                            ? <span className="text-zinc-400">Eliminado: {formatDate(u.fecha_eliminacion)}</span>
                            : formatDate(u.last_login)
                          }
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            {u.eliminado ? (
                              // Solo mostrar botón de restaurar para usuarios eliminados
                              isGerencia() && (
                                <button
                                  onClick={() => handleRestoreUser(u.id)}
                                  className="w-9 h-9 flex items-center justify-center text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                                  title="Restaurar usuario"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                              )
                            ) : (
                              <>
                                {/* Botones de Aprobar/Rechazar para usuarios pendientes */}
                                {u.estado_aprobacion === 'pendiente' && (
                                  <>
                                    <button
                                      onClick={() => handleAprobarUsuario(u.id, u.username)}
                                      className="w-9 h-9 flex items-center justify-center text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                                      title="Aprobar usuario"
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        if (confirm(`¿Rechazar la solicitud de ${u.username}?`)) {
                                          handleRechazarUsuario(u.id, u.username);
                                        }
                                      }}
                                      className="w-9 h-9 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Rechazar usuario"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => setSelectedUser(u)}
                                  className="w-9 h-9 flex items-center justify-center text-warm-400 hover:text-black hover:bg-warm-100 rounded-lg transition-colors"
                                  title="Ver perfil"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                            <button
                              onClick={() => {
                                setTargetUser(u);
                                setShowEditModal(true);
                              }}
                              className="w-9 h-9 flex items-center justify-center text-warm-400 hover:text-black hover:bg-warm-100 rounded-lg transition-colors"
                              title="Editar"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            {isGerencia() && (
                              <button
                                onClick={() => openPermissionsModal(u)}
                                className="w-9 h-9 flex items-center justify-center text-warm-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                title="Gestionar permisos"
                                data-testid={`permisos-${u.id}`}
                              >
                                <Lock className="w-4 h-4" />
                              </button>
                            )}
                            {isGerencia() && (
                              <button
                                onClick={() => {
                                  setTargetUser(u);
                                  setShowPasswordModal(true);
                                }}
                                className="w-9 h-9 flex items-center justify-center text-warm-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                title="Cambiar contraseña"
                              >
                                <Key className="w-4 h-4" />
                              </button>
                            )}
                            {/* Botón Eliminar */}
                            {canDeleteUser(u) && (
                              <button
                                onClick={() => {
                                  setUserToDelete(u);
                                  setShowDeleteModal(true);
                                }}
                                className="w-9 h-9 flex items-center justify-center text-warm-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Eliminar usuario"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-warm-100">
              {filteredUsers.map((u, index) => {
                const roleConfig = ROLE_CONFIG[u.role] || ROLE_CONFIG.asesor;
                const RoleIcon = roleConfig.icon;
                return (
                  <div 
                    key={u.id}
                    className="p-4 hover:bg-warm-50/50 transition-colors animate-fade-up"
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-warm-100 flex items-center justify-center overflow-hidden">
                        {u.profile_photo ? (
                          <img src={u.profile_photo} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-warm-600 text-lg font-medium">
                            {u.username?.charAt(0)?.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-black truncate">{u.username}</p>
                        <p className="text-sm text-warm-500 truncate">{u.email}</p>
                      </div>
                      {u.eliminado ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-[9px] uppercase tracking-wider font-semibold rounded-full bg-zinc-100 text-zinc-600">
                          <Trash2 className="w-3 h-3" />
                          Eliminado
                        </span>
                      ) : u.estado_aprobacion === 'pendiente' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-[9px] uppercase tracking-wider font-semibold rounded-full bg-amber-50 text-amber-700">
                          <Clock className="w-3 h-3" />
                          Pendiente
                        </span>
                      ) : (
                        <span className={`inline-flex items-center px-2 py-1 text-[9px] uppercase tracking-wider font-semibold rounded-full ${
                          u.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {u.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-[9px] uppercase tracking-wider font-semibold rounded-full border ${roleConfig.color}`}>
                          <RoleIcon className="w-3 h-3" />
                          {roleConfig.label}
                        </span>
                        {u.force_password_change && (
                          <span className="inline-flex items-center gap-1 text-[9px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                            <ShieldAlert className="w-3 h-3" />
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        {u.eliminado ? (
                          // Solo restaurar para eliminados
                          isGerencia() && (
                            <button
                              onClick={() => handleRestoreUser(u.id)}
                              className="w-10 h-10 flex items-center justify-center text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Restaurar"
                            >
                              <RotateCcw className="w-5 h-5" />
                            </button>
                          )
                        ) : (
                          <>
                            {/* Botones Aprobar/Rechazar para pendientes */}
                            {u.estado_aprobacion === 'pendiente' && (
                              <>
                                <button
                                  onClick={() => handleAprobarUsuario(u.id, u.username)}
                                  className="w-10 h-10 flex items-center justify-center text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                                  title="Aprobar"
                                >
                                  <Check className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => {
                                    if (confirm(`¿Rechazar la solicitud de ${u.username}?`)) {
                                      handleRechazarUsuario(u.id, u.username);
                                    }
                                  }}
                                  className="w-10 h-10 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Rechazar"
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => setSelectedUser(u)}
                              className="w-10 h-10 flex items-center justify-center text-warm-400 hover:text-black hover:bg-warm-100 rounded-lg transition-colors"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => {
                                setTargetUser(u);
                                setShowEditModal(true);
                              }}
                              className="w-10 h-10 flex items-center justify-center text-warm-400 hover:text-black hover:bg-warm-100 rounded-lg transition-colors"
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            {isGerencia() && (
                              <button
                                onClick={() => {
                                  setTargetUser(u);
                                  setShowPasswordModal(true);
                                }}
                                className="w-10 h-10 flex items-center justify-center text-warm-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              >
                                <Key className="w-5 h-5" />
                              </button>
                            )}
                            {canDeleteUser(u) && (
                              <button
                                onClick={() => {
                                  setUserToDelete(u);
                                  setShowDeleteModal(true);
                                }}
                                className="w-10 h-10 flex items-center justify-center text-warm-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Eliminar"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      {showPasswordModal && targetUser && (
        <PasswordChangeModal
          user={targetUser}
          onClose={() => {
            setShowPasswordModal(false);
            setTargetUser(null);
          }}
          onSuccess={fetchUsers}
        />
      )}

      {showEditModal && targetUser && (
        <EditUserModal
          user={targetUser}
          onClose={() => {
            setShowEditModal(false);
            setTargetUser(null);
          }}
          onSuccess={fetchUsers}
          isGerenciaUser={isGerencia()}
        />
      )}

      {/* Modal de Gestión de Permisos */}
      {showPermissionsModal && permissionsUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="p-6 border-b border-warm-100 bg-gradient-to-r from-purple-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-black">Gestionar Permisos</h2>
                    <p className="text-sm text-warm-500">{permissionsUser.username} • {ROLE_CONFIG[permissionsUser.role]?.label || permissionsUser.role}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowPermissionsModal(false);
                    setPermissionsUser(null);
                    setPermissionsData(null);
                    setCustomPermissions({});
                  }}
                  className="p-2 hover:bg-warm-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loadingPermissions ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : permissionsData ? (
                <div className="space-y-6">
                  {/* Info */}
                  <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
                    <p className="text-sm text-purple-800">
                      <strong>Nota:</strong> Los permisos personalizados sobreescriben los del rol. Los cambios con fondo morado son personalizados.
                    </p>
                  </div>

                  {/* Agrupar permisos por categoría */}
                  {['Fichas', 'Cotizaciones', 'Descargas', 'Admin', 'Aliados'].map(categoria => {
                    const permisosCategoria = permissionsData.lista_permisos.filter(p => p.categoria === categoria);
                    if (permisosCategoria.length === 0) return null;
                    
                    return (
                      <div key={categoria} className="space-y-3">
                        <h3 className="text-sm font-semibold text-black uppercase tracking-wider flex items-center gap-2">
                          {categoria === 'Fichas' && <Eye className="w-4 h-4" />}
                          {categoria === 'Cotizaciones' && <Settings className="w-4 h-4" />}
                          {categoria === 'Descargas' && <History className="w-4 h-4" />}
                          {categoria === 'Admin' && <ShieldCheck className="w-4 h-4" />}
                          {categoria === 'Aliados' && <Users className="w-4 h-4" />}
                          {categoria}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {permisosCategoria.map(permiso => {
                            const value = getPermissionValue(permiso.key);
                            const isCustom = isPermissionCustomized(permiso.key);
                            
                            return (
                              <button
                                key={permiso.key}
                                onClick={() => togglePermission(permiso.key)}
                                className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                  isCustom
                                    ? value
                                      ? 'bg-purple-100 border-purple-300'
                                      : 'bg-purple-50 border-purple-200'
                                    : value
                                      ? 'bg-emerald-50 border-emerald-200'
                                      : 'bg-warm-50 border-warm-200'
                                }`}
                              >
                                <span className="text-sm text-black">{permiso.label}</span>
                                <div className={`w-8 h-5 rounded-full flex items-center transition-colors ${
                                  value ? 'bg-emerald-500' : 'bg-warm-300'
                                }`}>
                                  <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                                    value ? 'translate-x-3.5' : 'translate-x-0.5'
                                  }`} />
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-warm-500 py-8">Error al cargar permisos</p>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-warm-100 bg-warm-50/50 flex items-center justify-between">
              <button
                onClick={resetPermissions}
                disabled={savingPermissions || Object.keys(customPermissions).length === 0}
                className="px-4 py-2 text-sm text-warm-600 hover:text-black hover:bg-warm-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Restaurar al rol
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowPermissionsModal(false);
                    setPermissionsUser(null);
                    setPermissionsData(null);
                    setCustomPermissions({});
                  }}
                  className="px-4 py-2 text-sm text-warm-600 hover:bg-warm-100 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={savePermissions}
                  disabled={savingPermissions}
                  className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="guardar-permisos"
                >
                  {savingPermissions ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Guardar permisos
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
            <div className="p-6 border-b border-warm-100 bg-gradient-to-r from-red-50 to-white">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-black">Eliminar Usuario</h2>
                  <p className="text-sm text-warm-500">{userToDelete.username} • {userToDelete.email}</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>¿Estás seguro de que deseas eliminar este usuario?</strong>
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Esta acción puede afectar el acceso del usuario a la plataforma.
                </p>
              </div>

              {userToDelete.role === 'gerencia' && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-purple-800">
                    <strong>Nota:</strong> Este usuario tiene rol de Gerencia. Solo se puede eliminar si hay otros usuarios Gerencia activos.
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-xs uppercase tracking-wider text-warm-500 mb-2">
                  Motivo de eliminación (opcional)
                </label>
                <textarea
                  value={deleteMotivo}
                  onChange={(e) => setDeleteMotivo(e.target.value)}
                  placeholder="Ej: Usuario de prueba, cuenta duplicada, etc."
                  className="w-full px-4 py-3 bg-warm-50 border border-warm-200 rounded-xl text-sm focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all resize-none"
                  rows={2}
                />
              </div>

              <p className="text-xs text-warm-500 mb-4">
                Si el usuario tiene cotizaciones, fichas o ventas asociadas, será eliminado lógicamente y sus datos se conservarán para auditoría.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                    setDeleteMotivo('');
                  }}
                  className="flex-1 px-4 py-3 text-sm font-medium text-warm-600 hover:bg-warm-100 rounded-xl transition-colors"
                  disabled={deletingUser}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={deletingUser}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deletingUser ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Eliminar usuario
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
